import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';

type Channel = 'global' | 'clan' | 'system' | 'dm' | 'projects' | 'guide' | 'broadcast';

export interface ChatMessage {
  id: string;
  channel: Channel;
  userId: string;
  username: string;
  content: string;
  timestamp: number;
}

const WS_URL = import.meta.env.PROD
  ? 'wss://madboat-infinichat-api-production.fidelismadboat.workers.dev/rooms/main/ws'
  : 'ws://localhost:8787/rooms/main/ws';

export function useInfiniChat() {
  const { user, token } = useAuthStore();
  const [messages, setMessages] = useState<Map<Channel, ChatMessage[]>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [currentChannel, setCurrentChannel] = useState<Channel>('global');
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  // Connect to WebSocket with auto-reconnect
  useEffect(() => {
    if (!user || !token) {
      console.log('[InfiniChat] Missing user or token, skipping connection');
      return;
    }

    // Ensure user.name exists, fallback to masterkey or id
    const username = user.name || (user as any).masterkey || user.id || 'Anonymous';

    const connect = () => {
      const url = `${WS_URL}?userId=${user.id}&username=${encodeURIComponent(username)}&sessionId=${token}`;
      console.log('[InfiniChat] Connecting to:', url);
      console.log('[InfiniChat] User info:', { id: user.id, name: user.name, username });
      console.log('[InfiniChat] Reconnect attempt:', reconnectAttemptsRef.current);

      const ws = new WebSocket(url);

      ws.onopen = () => {
        console.log('[InfiniChat] ✅ Connected successfully');
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0; // Reset on successful connection
      };

      ws.onmessage = (event) => {
        console.log('[InfiniChat] 📨 Received message:', event.data);
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'history') {
            console.log('[InfiniChat] 📜 Received history for channel:', data.channel, 'messages:', data.messages.length);
            // Received message history for a channel
            setMessages(prev => {
              const newMap = new Map(prev);
              newMap.set(data.channel, data.messages);
              return newMap;
            });
          } else if (data.type === 'message') {
            console.log('[InfiniChat] 💬 New message in channel:', data.channel);
            // Received new message
            setMessages(prev => {
              const newMap = new Map(prev);
              const channelMessages = newMap.get(data.channel) || [];
              newMap.set(data.channel, [...channelMessages, data]);
              return newMap;
            });
          } else if (data.type === 'system_message') {
            console.log('[InfiniChat] 🔔 System message received:', data.content);
            // System broadcast message (welcome notifications, etc.)
            // Add to both global and system channels
            const channelsToUpdate: Channel[] = ['global', 'system'];
            setMessages(prev => {
              const newMap = new Map(prev);
              for (const ch of channelsToUpdate) {
                const channelMessages = newMap.get(ch) || [];
                newMap.set(ch, [...channelMessages, { ...data, channel: ch }]);
              }
              return newMap;
            });
          } else if (data.type === 'error') {
            console.log('[InfiniChat] ⚠️ Error from server:', data.code, data.message);
            // Handle rate limit error
            if (data.code === 'RATE_LIMITED') {
              setRateLimitError(data.message);
              // Clear error after the retry period
              setTimeout(() => {
                setRateLimitError(null);
              }, (data.retryAfter || 10) * 1000);
            } else if (data.code === 'USER_OFFLINE') {
              // Show user offline notification as system message
              setMessages(prev => {
                const newMap = new Map(prev);
                const dmMessages = newMap.get('dm') || [];
                newMap.set('dm', [...dmMessages, {
                  id: `system_${Date.now()}`,
                  channel: 'dm' as Channel,
                  userId: 'system',
                  username: 'Sistema',
                  content: data.message,
                  timestamp: Date.now(),
                }]);
                return newMap;
              });
            }
          }
        } catch (err) {
          console.error('[InfiniChat] ❌ Message error:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('[InfiniChat] ⚠️ Disconnected - Code:', event.code, 'Reason:', event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Auto-reconnect with exponential backoff (important for mobile)
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          console.log(`[InfiniChat] 🔄 Reconnecting in ${delay}ms...`);
          setConnectionError(`Reconectando... (${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttemptsRef.current++;
            connect();
          }, delay);
        } else {
          console.log('[InfiniChat] ❌ Max reconnect attempts reached');
          setConnectionError('Falha na conexão. Recarregue a página.');
        }
      };

      ws.onerror = (err) => {
        console.error('[InfiniChat] ❌ WebSocket error:', err);
        setConnectionError('Erro de conexão');
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      console.log('[InfiniChat] 🔌 Closing connection');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user, token]);

  // Join a channel
  const joinChannel = useCallback((channel: Channel) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;

    wsRef.current.send(JSON.stringify({
      type: 'join_channel',
      channel,
    }));

    setCurrentChannel(channel);
  }, []);

  // Send a message
  const sendMessage = useCallback((content: string, channel: Channel = currentChannel) => {
    console.log('[InfiniChat] 📤 Attempting to send message:', content, 'to channel:', channel);

    if (!wsRef.current) {
      console.error('[InfiniChat] ❌ WebSocket ref is null');
      return;
    }

    if (wsRef.current.readyState !== WebSocket.OPEN) {
      console.error('[InfiniChat] ❌ WebSocket not open. State:', wsRef.current.readyState);
      return;
    }

    if (!content.trim()) {
      console.log('[InfiniChat] ⚠️ Empty message, skipping');
      return;
    }

    const message = {
      type: 'message',
      channel,
      content: content.trim(),
    };

    console.log('[InfiniChat] ✅ Sending message:', message);
    wsRef.current.send(JSON.stringify(message));
  }, [currentChannel]);

  // Add system message (for local notifications like friend online)
  const addSystemMessage = useCallback((content: string, channel: Channel = currentChannel) => {
    const systemMessage: ChatMessage = {
      id: `system_${Date.now()}_${Math.random()}`,
      channel,
      userId: 'system',
      username: 'Sistema',
      content,
      timestamp: Date.now(),
    };

    setMessages(prev => {
      const newMap = new Map(prev);
      const channelMessages = newMap.get(channel) || [];
      newMap.set(channel, [...channelMessages, systemMessage]);
      return newMap;
    });
  }, [currentChannel]);

  // Get messages for current channel
  const currentMessages = messages.get(currentChannel) || [];

  return {
    messages: currentMessages,
    isConnected,
    connectionError,
    currentChannel,
    sendMessage,
    joinChannel,
    addSystemMessage,
    rateLimitError,
  };
}
